import { useCallback, useEffect, useRef, useState } from "react";

import * as api from "../../lib/api.js";
import type { MeetingNoteOut, MeetingNotesOut } from "../contract/api";
import { RESULT_POLL_MS, RESULT_POLL_STEADY_ATTEMPTS } from "../result-poll";

/** What went wrong last, as the tab words it. */
export type NotesProblem = "load_failed" | "rate_limited" | "ask_failed" | null;

export interface MeetingNotes {
  /** The styles and this meeting's notes, once loaded. */
  data: MeetingNotesOut | null;
  problem: NotesProblem;
  /** A request for one style is on its way. */
  asking: string | null;
  /** Something is still being written, and the poll's patience has run out. */
  stalled: boolean;
  /** Load again: after a failure, or to check on notes the poll gave up on. */
  reload(): void;
  /** Ask for one style's notes: start, join, or (with `update`) write them again. */
  ask(style: string, update?: boolean): Promise<void>;
  /** The notes as they stand, loading them first if nobody has (Export). Never throws. */
  ensure(): Promise<MeetingNotesOut | null>;
}

/** `data` with one style's notes replaced by the server's newer answer. */
export function mergeNote(data: MeetingNotesOut, note: MeetingNoteOut): MeetingNotesOut {
  const others = data.notes.filter((n) => n.style !== note.style);
  return { ...data, notes: [...others, note] };
}

function writing(data: MeetingNotesOut | null): boolean {
  return Boolean(data?.notes.some((note) => note.status === "writing"));
}

/**
 * A meeting's notes in a style (#774), loaded when the Detailed tab first opens and kept
 * at the screen's level, so Export can include them without the tab having been opened.
 *
 * While any style is being written it polls the notes at the result poll's own settled
 * cadence and patience (`result-poll.ts`); past that it stops and says so (`stalled`),
 * and `reload` checks again.
 *
 * Every request carries the meeting it was made for and a sequence number:
 * - an answer for a meeting the screen has since left is dropped, so another meeting's
 *   notes can never show here (or be updated from here);
 * - a read that started before the latest ask is dropped, so an older poll cannot put
 *   "ready" back over the "writing" the ask just answered with.
 */
export function useMeetingNotes(sessionId: string, active: boolean): MeetingNotes {
  const [data, setData] = useState<MeetingNotesOut | null>(null);
  const [problem, setProblem] = useState<NotesProblem>(null);
  const [asking, setAsking] = useState<string | null>(null);
  const [stalled, setStalled] = useState(false);
  const [nonce, setNonce] = useState(0);
  const current = useRef(sessionId);
  const seq = useRef(0);
  const lastAsk = useRef(0);
  const polls = useRef(0);
  const loaded = useRef<{ id: string; promise: Promise<MeetingNotesOut | null> } | null>(null);

  // Another meeting starts clean: its own data, problem, polls and requests.
  useEffect(() => {
    current.current = sessionId;
    lastAsk.current = 0;
    polls.current = 0;
    loaded.current = null;
    setData(null);
    setProblem(null);
    setAsking(null);
    setStalled(false);
  }, [sessionId]);

  const fetchNotes = useCallback((): Promise<MeetingNotesOut | null> => {
    const id = sessionId;
    const mine = ++seq.current;
    const promise = api.getNotes(id).then(
      (out) => {
        if (current.current !== id) return null;
        if (mine < lastAsk.current) return out; // started before the latest ask: stale
        setData(out);
        setProblem((p) => (p === "load_failed" ? null : p));
        return out;
      },
      () => {
        if (current.current === id) setProblem("load_failed");
        return null;
      },
    );
    loaded.current = { id, promise };
    return promise;
  }, [sessionId]);

  // Read afresh whenever the tab opens: someone else in the meeting may have asked since.
  useEffect(() => {
    if (!active) return;
    void fetchNotes();
  }, [active, sessionId, nonce, fetchNotes]);

  // Poll while anything is being written; stop when nothing is, or patience runs out.
  const isWriting = writing(data);
  useEffect(() => {
    if (!isWriting) {
      polls.current = 0;
      setStalled(false);
      return;
    }
    if (polls.current >= RESULT_POLL_STEADY_ATTEMPTS) {
      setStalled(true);
      return;
    }
    const timer = setTimeout(() => {
      polls.current += 1;
      void fetchNotes();
    }, RESULT_POLL_MS);
    return () => clearTimeout(timer);
  }, [isWriting, data, fetchNotes]);

  const ask = useCallback(
    async (style: string, update = false) => {
      const id = sessionId;
      lastAsk.current = ++seq.current;
      setAsking(style);
      try {
        const note = await api.askNotes(id, style, { update });
        if (current.current !== id) return;
        setProblem(null);
        polls.current = 0;
        setStalled(false);
        setData((prev) => (prev ? mergeNote(prev, note) : prev));
      } catch (err) {
        if (current.current !== id) return;
        setProblem((err as { status?: number }).status === 429 ? "rate_limited" : "ask_failed");
      } finally {
        if (current.current === id) setAsking(null);
      }
    },
    [sessionId],
  );

  const ensure = useCallback(async () => {
    if (data) return data;
    const pending = loaded.current?.id === sessionId ? loaded.current.promise : fetchNotes();
    return pending.catch(() => null);
  }, [data, sessionId, fetchNotes]);

  const reload = useCallback(() => {
    setProblem(null);
    setStalled(false);
    polls.current = 0;
    setNonce((n) => n + 1);
  }, []);

  return { data, problem, asking, stalled, reload, ask, ensure };
}
